import React, { Component, ChangeEvent, FormEvent } from "react";
import axios from "axios";
import * as config from "../../config";

interface BulkUploadProps {
  jwtToken: string;
}

interface UploadResult {
  vin: string | null;
  success: boolean;
  error?: string;
}

interface BulkUploadState {
  file: File | null;
  loading: boolean;
  results: UploadResult[];
}

class BulkUpload extends Component<BulkUploadProps, BulkUploadState> {
  constructor(props: BulkUploadProps) {
    super(props);
    this.state = {
      file: null,
      loading: false,
      results: [],
    };
  }

  componentDidMount() {
    const savedResults = localStorage.getItem("bulkUploadResults");
    if (savedResults) {
      this.setState({ results: JSON.parse(savedResults) });
    }
  }

  componentDidUpdate(_: BulkUploadProps, prevState: BulkUploadState) {
    if (prevState.results !== this.state.results) {
      localStorage.setItem(
        "bulkUploadResults",
        JSON.stringify(this.state.results)
      );
    }
  }

  handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      this.setState({ file: e.target.files[0] });
    }
  };

  handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { file } = this.state;

    if (!file) {
      this.setState({
        results: [{ vin: null, success: false, error: "No file selected" }],
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    this.setState({ loading: true, results: [] });

    try {
      const response = await axios.post(
        `${config.rootUrl}/v1/configuration/bulkuploadvin`,
        formData,
        {
          headers: {
            "x-api-key": config.apiKey,
            Authorization: "Bearer " + this.props.jwtToken,
          },
        }
      );

      if (response.data && Array.isArray(response.data.results)) {
        this.setState({ results: response.data.results });
      } else {
        this.setState({
          results: [
            { vin: null, success: false, error: "Unexpected server response" },
          ],
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      this.setState({
        results: [{ vin: null, success: false, error: "Error uploading file" }],
      });
    } finally {
      this.setState({ loading: false, file: null }, () => {
        const fileInput = document.getElementById(
          "fileInput"
        ) as HTMLInputElement;
        if (fileInput) fileInput.value = "";
      });
    }
  };

  clearResults = () => {
    this.setState({ results: [] }, () => {
      localStorage.removeItem("bulkUploadResults");
    });
  };

  render() {
    const { loading, results } = this.state;

    const styles: { [key: string]: React.CSSProperties } = {
      container: {
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        maxWidth: "650px",
        margin: "50px auto",
        padding: "30px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        position: "relative",
      },
      heading: {
        fontSize: "1.5rem",
        fontWeight: 600,
        marginBottom: "20px",
        textAlign: "center" as const,
        color: "#333",
      },
      note: {
        fontSize: "1.35rem",
        color: "#555",
        marginBottom: "10px",
        padding: "8px",
        background: "#f9f9f9",
        border: "1px dashed #ccc",
        borderRadius: "6px",
      },
      input: {
        padding: "10px",
        border: "1px solid #dcdcdc",
        borderRadius: "6px",
        outline: "none",
        fontSize: "1.25rem",
      },
      button: {
        padding: "10px",
        background: loading ? "#888" : "#0066ff",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        fontWeight: 600,
        cursor: loading ? "not-allowed" : "pointer",
        fontSize: "1.25rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      spinner: {
        width: "18px",
        height: "18px",
        border: "3px solid rgba(255,255,255,0.3)",
        borderTop: "3px solid #fff",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      },
      resultWrapper: {
        marginTop: "15px",
        position: "relative",
      },
      closeBtn: {
        position: "absolute",
        right: "-10px",
        top: "-10px",
        cursor: "pointer",
        fontWeight: "bold",
        color: "#333",
        background: "white",
        border: "1px solid #ccc",
        borderRadius: "50%",
        width: "24px",
        height: "24px",
        textAlign: "center",
        lineHeight: "20px",
        fontSize: "16px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      },
      resultList: {
        listStyle: "none",
        padding: 0,
        fontSize: "0.95rem",
        marginTop: "5px",
      },
      resultItemSuccess: {
        backgroundColor: "#e6f9ed",
        color: "#1e7e34",
        padding: "8px",
        borderRadius: "6px",
        marginBottom: "5px",
        border: "1px solid #28a745",
      },
      resultItemError: {
        backgroundColor: "#fceaea",
        color: "#a71d2a",
        padding: "8px",
        borderRadius: "6px",
        marginBottom: "5px",
        border: "1px solid #dc3545",
      },
    };

    return (
      <div style={styles.container}>
        <style>
          {`@keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }`}
        </style>

        <h2 style={styles.heading}>Upload Excel File</h2>

        <div style={styles.note}>
          <strong>Example row format:</strong> vin:{" "}
          <code>1HGCM82633A004352</code>, env: <code>devna_virginia</code>,
          status: <code>active</code>
        </div>

        <form
          onSubmit={this.handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: "15px" }}
        >
          <input
            id="fileInput"
            type="file"
            accept=".xlsx,.xls"
            onChange={this.handleFileChange}
            style={styles.input}
          />
          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? <div style={styles.spinner}></div> : "Upload"}
          </button>
        </form>

        {results.length > 0 && (
          <div style={styles.resultWrapper}>
            <button style={styles.closeBtn} onClick={this.clearResults}>
              ×
            </button>
            <ul style={styles.resultList}>
              {results.map((res, idx) => (
                <li
                  key={idx}
                  style={
                    res.success
                      ? styles.resultItemSuccess
                      : styles.resultItemError
                  }
                >
                  {res.success
                    ? `✅ ${res.vin} uploaded successfully`
                    : `❌ ${res.vin || "Unknown VIN"} failed: ${res.error}`}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
}

export default BulkUpload;
